import { useCallback, useState } from 'react';

/**
 * `extra` is a page's OWN third view (`IndexPageProps.extraView`) — Building Management's
 * Calendar. Only a page declaring one can be in it; a stored `extra` on any other page falls back.
 */
export type IndexViewType = 'table' | 'list' | 'extra';

/**
 * Table or list view, remembered per module — reference §3.
 *
 * View type is a layout switch, not a content switch: both views surface the same fields,
 * so switching never hides information.
 *
 * **The list is the default.** A reader arriving at a module is usually looking for a record
 * rather than comparing a column across rows, and the list answers that in two lines per
 * record without a horizontal scroll. The table is the deliberate choice, kept one click away
 * and remembered per module once taken.
 */
export function useIndexViewType(module: string, fallback: IndexViewType = 'list', hasExtra = false) {
  const key = `index.${module}.viewType`;

  const [viewType, setViewTypeState] = useState<IndexViewType>(() => {
    try {
      /*
        A stored value wins; anything else falls back to the caller's default. Index pages
        pass nothing and land on the list; an embedded table panel passes 'table', because a
        panel a page asked for as a table should not open as something else.
      */
      const stored = localStorage.getItem(key);
      if (stored === 'table' || stored === 'list') return stored;
      if (stored === 'extra' && hasExtra) return stored;
      return fallback === 'extra' && !hasExtra ? 'list' : fallback;
    } catch {
      return fallback;
    }
  });

  const setViewType = useCallback(
    (next: IndexViewType) => {
      setViewTypeState(next);

      try {
        localStorage.setItem(key, next);
      } catch {
        // Remembering the preference is a convenience, never a requirement.
      }
    },
    [key],
  );

  return { viewType, setViewType };
}

/**
 * Filter-rail visibility, remembered per module. Defaults hidden — reference §3.
 */
export function useFilterRail(module: string) {
  const key = `index.${module}.filterRail`;

  const [open, setOpenState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(key) === 'open';
    } catch {
      return false;
    }
  });

  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);

      try {
        localStorage.setItem(key, next ? 'open' : 'closed');
      } catch {
        // Ignored — see above.
      }
    },
    [key],
  );

  return { open, setOpen };
}
